import { Box, VStack } from "@chakra-ui/react";
import { ReactNode } from "react";
import { ACTION_BAR_HEIGHT, ACTION_BAR_MARGIN } from "../util/layoutConstants";

type ComponentWithChildrenProps = {
  children: ReactNode;
  fitToWindow?: boolean; // New optional prop to control window fitting behavior
};

// Header component
function Header({ children }: ComponentWithChildrenProps) {
  return (
    <Box
        backgroundColor={"action-bar-bg"}
        px={1}
        // Fixed height (not content-driven auto height) so this bar is always exactly the same
        // height as Sidebar.tsx's mobile hamburger button, and content is centered within it
        // regardless of what that content is - see layoutConstants.ts.
        h={ACTION_BAR_HEIGHT}
        // BaseTemplate's VStack is a column flexbox; on fitToWindow pages (h=100vh) Header+Body
        // together can exceed the available height, and flex items shrink by default - without
        // this, that shrinks the Header below its explicit height instead of Body (which has
        // room to spare/scroll internally), silently breaking the burger-button height match.
        flexShrink={0}
        display="flex"
        alignItems="center"
        // On small screens Sidebar renders BurgerMenu instead of the desktop Sidebar (see
        // Sidebar.tsx's DynamicMenu - same base/sm vs. md breakpoint split), with a fixed
        // hamburger IconButton pinned to the viewport's top-left corner, outside normal document
        // flow. Padding alone only pushes this box's *content* right - the card's own border/
        // shadow/rounded corner still start at the same top-left spot as the button, so the
        // button visually sits on top of the card rather than beside it. Shift the whole card
        // right instead (margin, not padding) and shrink it by the same amount so it still fits.
        ml={{ base: ACTION_BAR_HEIGHT, sm: ACTION_BAR_HEIGHT, md: 0 }}
        w={{ base: `calc(100% - ${ACTION_BAR_HEIGHT})`, sm: `calc(100% - ${ACTION_BAR_HEIGHT})`, md: "100%" }}
        border={"1px"}
        borderRadius={"12px"}
        borderColor={"action-bar-border"}
        boxShadow={"md"}
    >
        {children}
    </Box>
  )
}

// Body component that fills the remaining space -- or, with fitContent, only wraps its children:
// a small card (the upload page's drop box) then sits snugly instead of stretching to the window,
// and flexShrink 0 stops the VStack squeezing it when the sibling grid card wants the height.
type BodyProps = ComponentWithChildrenProps & { fitContent?: boolean };
function Body({ children, fitContent }: BodyProps) {
  return (
    <Box
        backgroundColor={"action-bar-bg"}
        p={2}
        border={"1px"}
        borderRadius={"12px"}
        borderColor={"action-bar-border"}
        boxShadow={"md"}
        h={fitContent ? "auto" : "100%"}
        flexShrink={fitContent ? 0 : 1}
    >
      {children}
    </Box>
  );
}

// BaseTemplate component that conditionally applies full height when fitToWindow is true
function BaseTemplate({ children, fitToWindow }: ComponentWithChildrenProps) {
  const height = fitToWindow ? "100vh" : "auto";

  return (
    <VStack
      align="stretch"
      spacing={2}
      // Must match ACTION_BAR_MARGIN - this is what puts the Header's top-left corner at the
      // same offset as the mobile hamburger button's fixed top-left position (both anchor to the
      // viewport's top-left corner by this same margin amount).
      p={ACTION_BAR_MARGIN}
      h={height}
    >
      {children}
    </VStack>
  );
}

BaseTemplate.Header = Header;
BaseTemplate.Body = Body;

export default BaseTemplate;
